import React from "react";
import { AlertTriangle, CircleHelp, FileWarning, Replace } from "lucide-react";

const fmt = (v) => (v == null ? "—" : (typeof v === "object" ? JSON.stringify(v) : String(v)));
const SKIP = new Set(["kind", "resolution", "component_code", "segment_id"]);

/** Rappresentazione fedele di un conflitto del merger: ogni chiave persistita, nessun campo presunto. */
const ConflictLine = ({ c, i }) => {
  const ref = c.component_code || c.segment_id || "";
  const details = Object.entries(c).filter(([k, v]) => !SKIP.has(k) && v != null && v !== "").map(([k, v]) => `${k}=${fmt(v)}`);
  return (
    <div className="font-mono text-[10px] break-anywhere" data-testid={`result-conflict-${i}`}>
      <b>{c.kind}</b>{ref ? ` ${ref}` : ""}{details.length ? ` · ${details.join(" · ")}` : ""}{c.resolution ? ` · ${c.resolution}` : ""}
    </div>
  );
};

/** Blocchi di attenzione: pagina mancante, conflitti, UNKNOWN, sostituzioni di segmento. Solo dati persistiti. */
export const ReviewAlerts = ({ d }) => {
  const ri = d.review_items;
  const replacements = Object.entries(d.merged_document.replacements || {});
  const conflictsTotal = ri.conflicts.length + ri.invalid_evidence_claims.length + ri.compact_contract_violations.length + ri.missing_segments.length;
  return (
    <div className="space-y-1.5" data-testid="result-alerts">
      {ri.missing_pages.length > 0 && (
        <div className="border-2 border-red-400 bg-red-50 p-2 text-red-900 text-[11px] flex gap-2" data-testid="result-alert-missing-page">
          <FileWarning size={14} className="mt-0.5 shrink-0" />
          <div><b>PACKAGE_INCOMPLETE_DOCUMENTS_REQUIRED — pagina {ri.missing_pages.join(", ")} mancante.</b> Ricevute {ri.pages_received}/{ri.pages_expected} pagine
            (cartigli «Foglio n / {ri.pages_expected}»). Il contenuto del foglio {ri.missing_pages.join(", ")} è sconosciuto: nulla è stato inventato.</div>
        </div>
      )}
      <div className={`border-2 p-2 text-[11px] flex gap-2 ${conflictsTotal ? "border-red-400 bg-red-50 text-red-900" : "border-emerald-300 bg-emerald-50 text-emerald-900"}`} data-testid="result-alert-conflicts">
        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
        <div className="min-w-0">
          <b>Conflitti merger: {ri.conflicts.length}</b> · evidenze non valide: {ri.invalid_evidence_claims.length} · violazioni contratto compatto: {ri.compact_contract_violations.length} · segmenti mancanti: {ri.missing_segments.length} · duplicati evitati: {ri.duplicates_prevented.length}
          {ri.conflicts.map((c, i) => <ConflictLine key={i} c={c} i={i} />)}
          {!conflictsTotal && <div className="text-[10px]">Nessun conflitto S0↔segmento rilevato dal merger (spessore/materiale dei componenti non letti nei segmenti: vedi UNKNOWN).</div>}
          <div className="text-[10px]">Sottostati: {ri.substates.join(", ") || "—"}</div>
        </div>
      </div>
      <div className="border-2 border-amber-400 bg-amber-50 p-2 text-amber-950 text-[11px] flex gap-2" data-testid="result-alert-unknown">
        <CircleHelp size={14} className="mt-0.5 shrink-0" />
        <div>
          <b>Campi UNKNOWN: {ri.unknown_count}</b> · domande aperte: {ri.open_questions_total} · tolleranze: {ri.tolerances.present_in_result ? "presenti" : "non presenti nel risultato"}
          <div className="text-[10px]">{ri.tolerances.note}</div>
          <div className="flex flex-wrap gap-1 mt-1">
            {ri.unknown_fields.map((u, i) => (
              <span key={i} className="px-1 border border-amber-400 bg-white text-[9px] font-mono" title={u.reason} data-testid={`result-unknown-${i}`}>{u.component_code.slice(-4)}·{u.path}</span>
            ))}
          </div>
        </div>
      </div>
      {replacements.map(([sid, rep], idx) => (
        <div key={sid} className="border-2 border-emerald-500 bg-emerald-50 p-2 text-emerald-950 text-[11px] flex gap-2" data-testid={idx === 0 ? "result-alert-replacement" : `result-alert-replacement-${sid}`}>
          <Replace size={14} className="mt-0.5 shrink-0" />
          <div>
            <b>Segmento {sid} sostituito dall'Owner: {rep.display_id} ({rep.attempt_segment_id}) è la sostituzione valida usata nella fusione.</b>
            <div className="text-[10px] font-mono break-all">{sid} originale: {rep.original_state} · response {rep.original_response_id} → <span className="line-through">superseded, escluso</span></div>
            <div className="text-[10px] font-mono break-all">{rep.display_id}: response {rep.attempt_response_id} · output_hash {rep.attempt_output_hash?.slice(0, 16)}… · flag {d.merged_document.human_review_flags.join(", ")}</div>
            <div className="text-[10px]">Nel documento fuso la provenienza del componente è etichettata «{sid}» (posizione di piano); l'output_hash coincide byte-per-byte con la sostituzione valida.</div>
          </div>
        </div>
      ))}
    </div>
  );
};
